/**
 * Media Carousel Component
 * File: assets/media-carousel.js          (REPLACE)
 *
 * ============================================================
 * REWRITTEN 25 OCT 2026 — THE VISIBLE SET IS NOW DYNAMIC
 * ============================================================
 *
 * WHAT WAS WRONG FOR THIS JOB
 *
 * The previous version captured its slides and thumbnails once at init:
 *
 *     const items = container.querySelectorAll("[data-carousel-item]");
 *     const total = items.length;
 *
 * and navigated by index into that frozen array. That is fine for a gallery
 * that never changes. It cannot survive filtering: hide four of seven slides
 * and "next" still walks all seven, landing on hidden ones, and the
 * thumbnail highlight points at the wrong photo.
 *
 * Every lookup now reads the live DOM instead, so hiding and showing media is
 * something the carousel simply tolerates.
 *
 * ALSO FIXED HERE
 *
 * The old code bailed with `if (total <= 1) return;` BEFORE wiring anything
 * up, which meant a product whose gallery started with one visible item never
 * became navigable even after more items appeared.
 */
(function () {
  "use strict";

  var ANIM = 500;

  function visibleItems(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll("[data-carousel-item]"),
      function (el) { return !el.hasAttribute("hidden"); }
    );
  }

  function visibleThumbs(carousel) {
    return Array.prototype.filter.call(
      carousel.querySelectorAll("[data-carousel-thumbnail]"),
      function (el) { return !el.hasAttribute("hidden"); }
    );
  }

  /**
   * Which slide a freshly filtered gallery should land on.
   *
   * ADDED 25 OCT 2026
   *
   * Resetting to index 0 lands on whatever comes first in the media order,
   * which is only the flavor's own photo by luck. Upload a flavor's photo
   * last and the shared nutrition panel and evolution graphic sit ahead of
   * it, so picking Sour Green Apple opened on a honey and figs illustration.
   *
   * This prefers the first visible slide that BELONGS to a flavor, and falls
   * back to 0 when none does. That covers every case without the carousel
   * needing to be told which flavor is showing:
   *
   *   - a flavor with its own photos  -> its first photo
   *   - a flavor with none yet        -> 0, the first shared photo
   *   - a product with no flavors     -> 0, unchanged behaviour
   *
   * It also means the client can upload and reorder media freely. Ordering
   * is a presentation choice, not something load-bearing that silently
   * breaks the day someone drops a new shared photo into the middle.
   */
  function preferredIndex(container) {
    var items = visibleItems(container);
    for (var i = 0; i < items.length; i++) {
      if ((items[i].getAttribute("data-gg-media-flavor") || "") !== "") return i;
    }
    return 0;
  }

  /**
   * Put flavor-specific media ahead of shared media, once, at init.
   *
   * ADDED 25 OCT 2026
   *
   * preferredIndex got the big slide right, but the thumbnail strip still
   * read in Shopify's media order, so Sour Green Apple's only photo sat
   * third behind the evolution graphic and the nutrition panel. The slide
   * and its thumbnail were the same photo, they just were not in the same
   * place, which looks like a bug whether or not it is one.
   *
   * The alternative was a rule for the client: "always drag shared photos
   * to the end of the media list." That works right up until someone
   * uploads a photo, because Shopify appends new media and nobody
   * remembers a convention that lives in a document.
   *
   * So it is enforced here instead. Relative order WITHIN each group is
   * preserved, so the client still controls which watermelon photo leads
   * and which shared photo follows. Only the boundary between the two
   * groups is decided for them.
   *
   * No-ops on a product with no flavors, and on one where every photo is
   * flavored, since in both cases there is no boundary to move.
   */
  function hoistFlavorMedia(carousel, container) {
    function split(nodes) {
      var arr = Array.prototype.slice.call(nodes);
      var flavored = arr.filter(function (el) {
        return (el.getAttribute("data-gg-media-flavor") || "") !== "";
      });
      var shared = arr.filter(function (el) {
        return (el.getAttribute("data-gg-media-flavor") || "") === "";
      });
      return flavored.length && shared.length ? flavored.concat(shared) : null;
    }

    /* appendChild on a node already in the document MOVES it, so appending
       in the desired order is the reorder. */
    var slides = split(container.querySelectorAll("[data-carousel-item]"));
    if (slides) slides.forEach(function (el) { container.appendChild(el); });

    var thumbWrap = carousel.querySelector("[data-carousel-thumbs]");
    if (!thumbWrap) return;
    var thumbs = split(thumbWrap.querySelectorAll("[data-carousel-thumbnail]"));
    if (thumbs) thumbs.forEach(function (el) { thumbWrap.appendChild(el); });
  }

  var MediaCarousel = {
    init: function () {
      Array.prototype.forEach.call(
        document.querySelectorAll("[data-media-carousel]"),
        this.initCarousel.bind(this)
      );
    },

    initCarousel: function (carousel) {
      var container = carousel.querySelector("[data-carousel-container]");
      if (!container || carousel.__ggBound) return;
      carousel.__ggBound = true;

      hoistFlavorMedia(carousel, container);

      /* Starts on the first flavor-specific slide, not blindly on 0. This
         matters on first load as much as on a flavor change: an ad landing
         on ?variant=<Sour Green Apple> renders with that flavor already
         filtered server-side, and index 0 would be a shared photo. */
      var state = { index: preferredIndex(container), busy: false };
      carousel.__ggState = state;

      function paint() {
        var items = visibleItems(container);
        var thumbs = visibleThumbs(carousel);
        var arrows = carousel.querySelector("[data-carousel-arrows]");

        if (state.index >= items.length) state.index = 0;

        items.forEach(function (el, i) {
          var on = i === state.index;
          el.style.opacity = on ? "1" : "0";
          el.style.zIndex = on ? "1" : "0";
          el.style.transform = "";
          el.style.transition = "";
        });

        thumbs.forEach(function (el, i) {
          var on = i === state.index;
          el.classList.toggle("border-gum", on);
          el.classList.toggle("border-black", !on);
        });

        /* Arrows are pointless on a single photo, and the visible count
           changes with the flavor, so this is re-evaluated every paint. */
        if (arrows) {
          if (items.length > 1) arrows.removeAttribute("hidden");
          else arrows.setAttribute("hidden", "");
        }
      }

      function goTo(next, direction) {
        var items = visibleItems(container);
        if (items.length < 2 || state.busy) return;

        var total = items.length;
        next = ((next % total) + total) % total;
        if (next === state.index) return;

        var from = items[state.index];
        var to = items[next];
        if (!from || !to) { state.index = next; paint(); return; }

        state.busy = true;

        to.style.transform =
          direction === "prev" ? "translateX(-100%)" : "translateX(100%)";
        to.style.opacity = "1";
        to.style.zIndex = "2";
        from.style.zIndex = "1";

        /* Forces the starting transform to be applied before the transition
           is added, otherwise the browser collapses both into one frame and
           the slide jumps rather than moves. */
        void to.offsetHeight;

        var ease = "transform 0.5s cubic-bezier(0.455, 0.03, 0.515, 0.955)";
        from.style.transition = ease;
        to.style.transition = ease;
        from.style.transform =
          direction === "prev" ? "translateX(100%)" : "translateX(-100%)";
        to.style.transform = "translateX(0)";

        window.setTimeout(function () {
          state.index = next;
          state.busy = false;
          paint();
        }, ANIM);
      }

      carousel.__ggGoTo = goTo;
      carousel.__ggPaint = paint;

      var prev = carousel.querySelector("[data-carousel-prev]");
      var next = carousel.querySelector("[data-carousel-next]");
      if (prev) prev.addEventListener("click", function () { goTo(state.index - 1, "prev"); });
      if (next) next.addEventListener("click", function () { goTo(state.index + 1, "next"); });

      /* Delegated, because which thumbnails exist changes with the flavor and
         rebinding each one on every change is how listeners get duplicated. */
      carousel.addEventListener("click", function (e) {
        var thumb = e.target.closest ? e.target.closest("[data-carousel-thumbnail]") : null;
        if (!thumb || thumb.hasAttribute("hidden")) return;
        var idx = visibleThumbs(carousel).indexOf(thumb);
        if (idx < 0 || idx === state.index) return;
        goTo(idx, idx > state.index ? "next" : "prev");
      });

      paint();
    },

    /**
     * Show only media belonging to `flavor`, plus anything unlabelled.
     * Unlabelled media is shared on purpose: the nutrition panel and the
     * lifestyle shots are not flavor specific.
     */
    filter: function (carousel, flavor) {
      if (!carousel) return;
      var container = carousel.querySelector("[data-carousel-container]");
      if (!container) return;

      Array.prototype.forEach.call(
        carousel.querySelectorAll("[data-gg-media-flavor]"),
        function (el) {
          var own = el.getAttribute("data-gg-media-flavor") || "";
          var show = own === "" || own === flavor;
          if (show) el.removeAttribute("hidden");
          else el.setAttribute("hidden", "");
        }
      );

      var state = carousel.__ggState;
      if (state) state.index = preferredIndex(container);
      if (carousel.__ggPaint) carousel.__ggPaint();
    },

    /** Jump to a specific media id, used when a variant has a featured image. */
    jumpToMedia: function (carousel, mediaId) {
      if (!carousel || !mediaId) return;
      var container = carousel.querySelector("[data-carousel-container]");
      if (!container) return;
      var items = visibleItems(container);
      for (var i = 0; i < items.length; i++) {
        if (items[i].getAttribute("data-media-id") === String(mediaId)) {
          if (carousel.__ggGoTo) {
            carousel.__ggGoTo(i, i > (carousel.__ggState || {}).index ? "next" : "prev");
          }
          return;
        }
      }
    },
  };

  window.MediaCarousel = MediaCarousel;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { MediaCarousel.init(); });
  } else {
    MediaCarousel.init();
  }
})();